import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { GetFilterOptionsResponse } from '@erp/rpc';
import type { MultiSelectOption } from '@/components/common/MultiSelect';

/**
 * Filter dropdown options (divisions, suppliers, trading partners) shared by
 * every S&D page.
 *
 * Held stale-free for 5 minutes because it is expensive to produce and almost
 * never changes: the backend derives ~160 dropdown rows by scanning 400k+
 * `sd_e844d` index entries (~88 ms for distinct suppliers + ~166 ms for
 * trading-partner counts, measured on prod). The global default `staleTime` is
 * 0, so without this every navigation between S&D pages paid that again — more
 * than the 849 list's entire query budget. A newly-added supplier or division
 * can take up to 5 minutes (or one reload) to appear in a dropdown, which is
 * an acceptable trade for options that change on the order of weeks.
 */
const FILTER_OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

export function useFilterOptions() {
  const query = useQuery(
    orpc.shipAndDebit.e844sdTransactions.getFilterOptions.queryOptions({
      input: {},
      staleTime: FILTER_OPTIONS_STALE_TIME_MS,
    }),
  );
  return {
    data: query.data as GetFilterOptionsResponse | undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useDivisionOptions(
  filterOptionsData: GetFilterOptionsResponse | undefined,
): MultiSelectOption[] {
  return useMemo(() => {
    const codes = filterOptionsData?.division ?? [];
    const labels = filterOptionsData?.division_labels ?? {};
    return codes.map((code) => ({
      id: code,
      label: labels[code] ?? code,
    }));
  }, [filterOptionsData]);
}
